/* eslint-disable no-restricted-syntax */
import { IFontManager } from '@src/slide/content-viewer/service/font-manager/core/font-manager.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { ILogger, SlideDataServices } from '../../../core/slide-data-services.core';
import { ISlideAppService } from '../../../core/interfaces/slide-app-service.interface';
import { IEmbeddedFontFamily } from '@tencent/slide-sdk-model';
import { formatError } from '../../../utils/trace-format';
import { assert } from 'ts-essentials';

const MODULE = '[EmbeddedFontManager]';

class EmbeddedFontManager {
    public constructor(private logger: ILogger, private slideApp: ISlideAppService) {
        this.loadEmbeddedFont().catch((error) => {
            this.logger.error(MODULE, formatError(error));
        });
    }

    /* eslint-disable complexity */
    public async loadEmbeddedFont(): Promise<void> {
        const fontManager = this.getFontManager();
        const fontFamilies: IEmbeddedFontFamily[] = this.getEmbeddedFontFamilies();
        if (!fontFamilies?.length || !fontManager) {
            return;
        }

        const fontFaces: FontFace[] = [];
        for (const fontFamily of fontFamilies) {
            for (const [category, data] of Object.entries(fontFamily.subfamilies)) {
                if (!data) {
                    continue;
                }

                const style = ['italic', 'boldItalic'].includes(category) ? 'italic' : 'normal';
                const weight = ['bold', 'boldItalic'].includes(category) ? 'bold' : 'normal';
                const src = data.src.endsWith('.fntdata') ? `${data.src}.ttf` : data.src;

                const font = new FontFace(fontFamily.typeface, `url(${src})`, { style, weight });
                fontFaces.push(font);
            }
        }

        const results = await Promise.allSettled(fontFaces.map(fontFace => fontFace.load()));

        for (const [i, result] of results.entries()) {
            const fontFace = fontFaces[i];
            if (result.status !== 'fulfilled') {
                this.logger.error(
                    MODULE,
                    `error occurs while loading font "${fontFace.family}" style=${fontFace.style} weight=${fontFace.weight}`,
                    formatError(result.reason),
                );
                continue;
            }

            document.fonts.add(fontFace);
            fontManager.downloadedFontFamilies.push(fontFace.family);
        }
    }

    private getFontManager(): IFontManager | undefined {
        const appService = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appService.viewableServiceLayer.fontManager;
    }

    private getEmbeddedFontFamilies(): IEmbeddedFontFamily[] {
        const presentationStoreService = SlideDataServices.SDKDataServices.PresentationStoreService();
        assert(presentationStoreService, 'presentationStoreService should not be undefined');
        return presentationStoreService.getPresentation().getEmbeddedFontFamilies();
    }
}

export default EmbeddedFontManager;
